import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { openWithCacheBuster } from "./cacheBuster";
import { Link2, ExternalLink, AlertTriangle } from "lucide-react";
import type { PageIssue, NofollowInternalDetails } from "@/types/technicalAudit";

interface NofollowInternalCardProps {
	issue: PageIssue;
}

export function NofollowInternalCard({ issue }: NofollowInternalCardProps) {
	const details = issue.issue_details as NofollowInternalDetails;

	return (
		<div className="space-y-4">
			<div className="flex items-start gap-3 rounded-xl p-4 border bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-800">
				<div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center bg-amber-100 dark:bg-amber-900/40">
					<AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400" />
				</div>
				<div className="flex-1">
					<h4 className="font-semibold text-amber-900 dark:text-amber-300">Nofollow Internal Link</h4>
					<p className="text-sm mt-1 text-amber-700 dark:text-amber-400">
						An internal link has <code className="px-1 py-0.5 bg-amber-100 dark:bg-amber-900/40 rounded text-xs">rel="nofollow"</code>, which blocks link equity flow between your own pages.
					</p>
				</div>
			</div>

			<Card className="border-border shadow-sm">
				<CardHeader className="pb-3">
					<CardTitle className="text-sm font-semibold text-foreground flex items-center gap-2">
						<div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/40 flex items-center justify-center">
							<Link2 className="w-4 h-4 text-blue-600 dark:text-blue-400" />
						</div>
						Link Details
					</CardTitle>
				</CardHeader>
				<CardContent className="pt-0 space-y-3">
					<div>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Source Page</p>
						<a
							href={details.page_url}
							onClick={(e) => openWithCacheBuster(e, details.page_url)}
							target="_blank"
							rel="noopener noreferrer"
							className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
						>
							<p className="text-sm font-mono break-all text-foreground flex-1">{details.page_url}</p>
							<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
						</a>
					</div>
					<div>
						<p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Destination</p>
						<a
							href={details.target_url}
							onClick={(e) => openWithCacheBuster(e, details.target_url)}
							target="_blank"
							rel="noopener noreferrer"
							className="group flex items-start gap-2 p-3 rounded-lg border bg-muted border-border hover:border-border hover:bg-muted transition-all"
						>
							<p className="text-sm font-mono break-all text-foreground flex-1">{details.target_url}</p>
							<ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0 mt-0.5" />
						</a>
					</div>
					{details.link_text && (
						<p className="text-xs text-muted-foreground">
							Anchor text: &ldquo;{details.link_text}&rdquo;
						</p>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
